import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../services/firebase";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

function LoginPage({ onLoginSuccess, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const styles = {
    page: {
      minHeight: "100vh" /* FIX: Allows scrolling on zoom/mobile */,
      width: "100%",
      background: "linear-gradient(180deg,#f7fafc 0%,#eef2ff 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px" /* FIX: Padding on mobile */,
      fontFamily:
        "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      color: "#0f172a",
      boxSizing: "border-box",
    },
    card: {
      width: "100%",
      maxWidth: "520px",
      borderRadius: "12px",
      background: "#ffffff",
      border: "1px solid rgba(15, 23, 42, 0.06)",
      boxShadow: "0 10px 30px rgba(2,6,23,0.06)",
      padding: "36px 34px 30px",
      boxSizing: "border-box",
      margin: "auto",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      gap: "12px",
      flexWrap: "wrap" /* FIX: Prevents overlap on small screens */,
    },
    product: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    productText: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },
    productName: {
      fontSize: "1rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: "#0f172a",
    },
    productTag: {
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      color: "#6b7280",
    },
    backButton: {
      borderRadius: "10px",
      border: "1px solid rgba(15,23,42,0.06)",
      background: "#ffffff",
      color: "#475569",
      fontSize: "0.82rem",
      padding: "6px 10px",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.15s ease",
    },
    title: {
      fontSize: "1.5rem",
      fontWeight: 700,
      marginTop: "8px",
      marginBottom: "18px",
      textAlign: "left",
      color: "#0f172a",
      lineHeight: 1.15,
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "8px",
      border: "1px solid rgba(15,23,42,0.06)",
      padding: "10px 12px",
      fontSize: "0.95rem",
      background: "#ffffff",
      color: "#0f172a",
      outline: "none",
      transition: "border-color 0.15s ease",
    },
    submitButton: {
      marginTop: "16px",
      width: "100%",
      padding: "12px 18px",
      borderRadius: "10px",
      border: "none",
      background: "#0369a1",
      color: "#ffffff",
      fontSize: "0.97rem",
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 8px 20px rgba(3,105,161,0.12)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      opacity: submitting ? 0.9 : 1,
    },
    footer: {
      marginTop: "24px",
      paddingTop: "12px",
      borderTop: "1px solid rgba(2,6,23,0.04)",
      fontSize: "0.85rem",
      color: "#64748b",
      lineHeight: 1.5,
      textAlign: "center",
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!auth) throw new Error("Auth not initialized");
      const userCred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      if (onLoginSuccess) onLoginSuccess(userCred.user);
    } catch (err) {
      setError("Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.product}>
            <img
              src="/logo.png"
              alt="Logo"
              style={{ width: 36, height: 36, objectFit: "contain" }}
            />
            <div style={styles.productText}>
              <div style={styles.productName}>Prior Art Report Generator</div>
              <div style={styles.productTag}>Secure access</div>
            </div>
          </div>
          {onBack && (
            <button type="button" style={styles.backButton} onClick={onBack}>
              <ArrowLeft size={16} />
              <span style={{ marginLeft: 6 }}>Back</span>
            </button>
          )}
        </div>

        <div style={styles.title}>Sign in to your workspace</div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "0.86rem",
                fontWeight: 600,
                marginBottom: "6px",
              }}
            >
              Email address
            </label>
            <input
              type="email"
              required
              placeholder="name@firm.com"
              style={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "0.86rem",
                fontWeight: 600,
                marginBottom: "6px",
              }}
            >
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="Enter your password"
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              style={{
                color: "#dc2626",
                fontSize: "0.85rem",
                background: "rgba(220, 38, 38, 0.1)",
                padding: "8px",
                borderRadius: "6px",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            style={styles.submitButton}
            disabled={submitting}
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div style={styles.footer}>
          This portal is intended for authorized users only. Activity may be
          logged.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
